import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Newspaper, Calendar as CalIcon, Globe, Sparkles, Radio, Tv, BookOpen, Clapperboard, GitCompareArrows, Type } from "lucide-react";

import { fetchRegions, fetchRandomIconicDate, postTimeJump, postWaitlist } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useEntitlement } from "@/context/EntitlementContext";
import Masthead from "@/components/Masthead";
import AuthMenu from "@/components/AuthMenu";
import OnThisDay from "@/components/OnThisDay";
import LandingTrailer from "@/components/LandingTrailer";
import NewsletterSignup from "@/components/NewsletterSignup";
import MonthlyReel from "@/components/MonthlyReel";
import PricingModal from "@/components/PricingModal";
import { getIconicList, ACCENT_STYLES } from "@/lib/iconic-dates";
import { format, parse, isValid } from "date-fns";
import { Cake, Mail, Check } from "lucide-react";

const MIN_DATE = "1900-01-01";

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

/**
 * parseFreeDate — accept messy human input and normalise to YYYY-MM-DD.
 * Returns null if unparseable.
 *
 *   "13/07/1985"     → "1985-07-13"
 *   "13-07-1985"     → "1985-07-13"
 *   "07/13/1985"     → "1985-07-13"  (heuristic: if first > 12, day-first)
 *   "13.07.1985"     → "1985-07-13"
 *   "July 13 1985"   → "1985-07-13"
 *   "13 July 1985"   → "1985-07-13"
 *   "1985-07-13"     → "1985-07-13"  (already ISO)
 */
function parseFreeDate(raw) {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  // ISO already
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // dd[.-/]mm[.-/]yyyy or mm[.-/]dd[.-/]yyyy
  const m = s.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);
  if (m) {
    let a = parseInt(m[1], 10), b = parseInt(m[2], 10);
    const y = m[3];
    // Heuristic: if first > 12 → day-first, else assume day-first (international)
    let dd, mm;
    if (a > 12 && b <= 12) { dd = a; mm = b; }
    else if (b > 12 && a <= 12) { dd = b; mm = a; }
    else { dd = a; mm = b; }
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
    return `${y}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  }
  // "July 13 1985" / "13 July 1985" — leverage Date
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return null;
}

export default function Landing() {
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(() => {
    try { return localStorage.getItem("tc_region") || "US"; } catch { return "US"; }
  });
  // Default to TODAY — "what happened on this day in history" is the killer
  // first-impression prompt. Users see the current date and get curious.
  const [date, setDate] = useState(todayISO);
  const [loading, setLoading] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingReason, setPricingReason] = useState("manual");
  const navigate = useNavigate();
  const { entitlement, refresh: refreshEntitlement } = useEntitlement();

  // Persist region choice so TV Ambient mode + other pages can pick it up
  useEffect(() => {
    try { localStorage.setItem("tc_region", region); } catch { /* private mode */ }
  }, [region]);

  // Preserve upgrade intent across sign-in. When the pricing modal triggered
  // sign-in, it added `?upgrade=1` (and optionally `?reason=…`) to the return
  // URL. On landing back here we auto-reopen the modal so the user can
  // actually pay — otherwise they got signed in but nothing prompted payment,
  // which looks exactly like "the app gave me free premium".
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("upgrade") === "1") {
      setPricingReason(p.get("reason") || "post_signin");
      setShowPricing(true);
      // Clean the URL so a refresh doesn't re-trigger the modal
      p.delete("upgrade");
      p.delete("reason");
      const clean = window.location.pathname + (p.toString() ? `?${p}` : "") + window.location.hash;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  useEffect(() => {
    trackEvent("page_view", { page: "landing" });
    // Retry once on cold-start / transient failures so the region dropdown
    // never stays empty behind a toast.
    const loadRegions = async (attempt = 1) => {
      try {
        const data = await fetchRegions();
        setRegions(data);
      } catch (err) {
        if (attempt < 2) {
          setTimeout(() => loadRegions(attempt + 1), 800);
        } else {
          toast.error("Failed to load regions");
        }
      }
    };
    loadRegions();
  }, []);

  const goJump = async (d, r) => {
    const usedDate = d || date;
    const usedRegion = r || region;
    const parsed = parse(usedDate, "yyyy-MM-dd", new Date());
    if (!isValid(parsed)) {
      toast.error("Please choose a valid date");
      return;
    }
    if (usedDate < MIN_DATE) {
      toast.error("Dates before 1900 are not supported");
      return;
    }
    if (usedDate > todayISO()) {
      toast.error("Cannot jump to the future");
      return;
    }
    setLoading(true);
    try {
      trackEvent("time_jump", { date: usedDate, region: usedRegion, source: d ? "iconic_or_surprise" : "manual" });
      const data = await postTimeJump({ date: usedDate, region: usedRegion });
      sessionStorage.setItem(`tc:${data.id}`, JSON.stringify(data));
      refreshEntitlement();  // update the remaining-quota indicator
      navigate(`/snapshot/${data.id}`);
    } catch (e) {
      // 402 → Free daily front-page quota exhausted. Show upgrade paywall.
      if (e?.response?.status === 402) {
        setPricingReason("quota_exhausted");
        setShowPricing(true);
        trackEvent("paywall_shown", { reason: "front_page_quota_exhausted" });
      } else {
        const msg = e?.response?.data?.detail;
        toast.error(typeof msg === "string" ? msg : "Time jump failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const surprise = async () => {
    try {
      const pick = await fetchRandomIconicDate();
      trackEvent("surprise_me", { date: pick.date, region: pick.region, label: pick.label });
      setDate(pick.date);
      setRegion(pick.region);
      toast(`Surprise! ${pick.label}`, { description: format(new Date(pick.date), "MMMM d, yyyy") });
      goJump(pick.date, pick.region);
    } catch {
      toast.error("Could not fetch a surprise date");
    }
  };

  const humanDate = (() => {
    try {
      return format(parse(date, "yyyy-MM-dd", new Date()), "EEEE, MMMM d, yyyy");
    } catch {
      return date;
    }
  })();

  return (
    <div className="min-h-screen w-full" data-testid="landing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        {/* Nav — floats above the masthead */}
        <div className="flex justify-end mb-4 gap-2 items-center">
          <Link
            to="/tv"
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
            style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            data-testid="nav-tv-guide"
          >
            <Tv size={14} /> <span className="hidden sm:inline">Watch on TV</span>
          </Link>
          <AuthMenu returnPath="/" />
        </div>

        <Masthead volumeDate={humanDate} />


        {/* Birthday hook — front-and-center */}
        <section className="mt-8" data-testid="birthday-hook">
          <div className="border-2 border-ink bg-press-red text-paper p-6 sm:p-8 flex flex-col md:flex-row items-stretch md:items-center gap-4 shadow-hard">
            <div className="flex items-center gap-3 flex-1">
              <div className="border-2 border-paper p-2"><Cake size={26} /></div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-paper/80">Try this first</div>
                <div className="font-headline text-2xl sm:text-3xl leading-tight">Read the newspaper from the day you were born.</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch">
              <input
                data-testid="birthday-input"
                type="date"
                min={MIN_DATE}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                className="bg-paper text-ink border-2 border-ink px-3 py-3 font-mono text-lg focus:outline-none"
                aria-label="Your birthday"
              />
              <button
                data-testid="birthday-go-btn"
                disabled={loading}
                onClick={() => { trackEvent("birthday_jump"); goJump(); }}
                className="bg-ink text-paper font-mono uppercase tracking-widest text-sm px-5 py-3 border-2 border-ink shadow-hard-sm disabled:opacity-70"
                style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              >
                {loading ? "…" : "See My Day →"}
              </button>
            </div>
          </div>
        </section>


        {/* Editorial intro */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-0 border-t-2 border-l-2 border-ink relative overflow-hidden">
          <LandingTrailer />
          <div className="md:col-span-8 border-r-2 border-b-2 border-ink p-6 sm:p-10 bg-paper-surface/60 backdrop-blur-[2px] relative z-[1]">
            <div className="flex items-center gap-3 mb-4">
              <span className="ink-stamp red">Vol. 24 · No. 1</span>
              <span className="font-mono uppercase tracking-widest text-xs text-ink-muted">— The Time Jump Bureau —</span>
            </div>
            <h1 className="font-headline font-black leading-[0.9] tracking-tighter text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[5.25rem] uppercase text-ink">
              Yesterday,
              <br />
              <span className="italic font-serif" style={{ fontStyle: "italic" }}>delivered</span>
              <br />
              on demand.
            </h1>
            <p className="mt-6 font-body text-lg sm:text-xl max-w-2xl leading-relaxed text-ink-secondary drop-cap">
              Pick a day since 1900 and any of eleven regions. In seconds, TimeCapsule 24 assembles the front page: weather, top news, the songs on the radio, movies at the picture-house, the shows on the tube, the bestsellers on the shelf — and the small things you forgot: gas prices, ticker tape, and the slang of the moment.
            </p>
          </div>

          <aside className="md:col-span-4 border-r-2 border-b-2 border-ink p-6 sm:p-8 bg-paper-alt/70 backdrop-blur-[2px] flex flex-col justify-between relative z-[1]">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-2">— Extra —</div>
              <h3 className="font-headline text-2xl leading-tight border-b-2 border-ink pb-2 mb-4">What is TimeCapsule?</h3>
              <ul className="space-y-3 font-body text-base">
                <li className="flex gap-3"><Newspaper size={18} className="mt-1 shrink-0" /> Front-page headlines for any date.</li>
                <li className="flex gap-3"><Radio size={18} className="mt-1 shrink-0" /> Top 5 songs of the week, by country.</li>
                <li className="flex gap-3"><Clapperboard size={18} className="mt-1 shrink-0" /> What was playing at the movies.</li>
                <li className="flex gap-3"><Tv size={18} className="mt-1 shrink-0" /> Popular TV shows on the air.</li>
                <li className="flex gap-3"><BookOpen size={18} className="mt-1 shrink-0" /> Bestsellers in bookshops.</li>
              </ul>
            </div>
            <div className="mt-6 pt-4 border-t border-dashed border-ink font-mono text-xs uppercase tracking-widest text-ink-muted">
              Assembled fresh · Wired daily · Since 1900
            </div>
          </aside>
        </section>

        {/* On This Day — auto-populated from today's UTC month/day */}
        <OnThisDay />

        {/* The Time Jump control */}
        <section className="mt-12" data-testid="time-jump-panel">
          <div className="flex items-end justify-between border-b-4 border-double border-ink pb-3 mb-6">
            <h2 className="font-headline text-3xl sm:text-4xl font-bold uppercase tracking-tight">The Time Jump Desk</h2>
            <span className="hidden sm:inline font-mono text-xs uppercase tracking-widest text-ink-muted">Please stamp your ticket clearly</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-0 border-t-2 border-l-2 border-ink">
            {/* Date input — native picker + manual free-form text */}
            <div className="md:col-span-5 border-r-2 border-b-2 border-ink p-6 sm:p-8 bg-paper-surface">
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ink-muted mb-3">
                <CalIcon size={14} /> Date of Journey
              </label>
              <input
                data-testid="date-input"
                id="tc-date-input"
                aria-label="Date of journey"
                type="date"
                value={date}
                min={MIN_DATE}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                className="bg-transparent border-0 border-b-2 border-ink rounded-none w-full font-mono text-2xl sm:text-3xl py-2 focus:outline-none focus:border-press-red"
              />
              <div className="mt-3">
                <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">
                  <Type size={11} /> Or type it — dd/mm/yyyy, &quot;July 13 1985&quot;, etc.
                </label>
                <input
                  data-testid="date-input-manual"
                  id="tc-date-input-manual"
                  aria-label="Type date freehand — dd/mm/yyyy or July 13 1985"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  placeholder="13/07/1985"
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const parsed = parseFreeDate(raw);
                    if (!parsed) return;
                    if (parsed < MIN_DATE || parsed > todayISO()) return;
                    setDate(parsed);
                    trackEvent("date_manual_input", { input: raw, parsed });
                    e.target.value = "";
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  className="bg-paper-alt border-2 border-ink px-2 py-1.5 w-full font-mono text-sm focus:outline-none focus:bg-white"
                />
              </div>
              <div className="mt-3 font-body italic text-ink-muted text-sm">{humanDate}</div>
            </div>

            {/* Region select */}
            <div className="md:col-span-4 border-r-2 border-b-2 border-ink p-6 sm:p-8 bg-paper-surface">
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ink-muted mb-3">
                <Globe size={14} /> Region / Edition
              </label>
              <select
                data-testid="region-select"
                id="tc-region-select"
                aria-label="Region — pick the country edition of the newspaper"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="bg-transparent border-0 border-b-2 border-ink rounded-none w-full font-mono text-xl sm:text-2xl py-2 focus:outline-none focus:border-press-red appearance-none cursor-pointer"
                style={{ backgroundImage: "none" }}
              >
                {regions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.flag} {r.name}
                  </option>
                ))}
              </select>
              <div className="mt-3 font-body italic text-ink-muted text-sm">
                Local editions with regional charts &amp; headlines.
              </div>
            </div>

            {/* Actions */}
            <div className="md:col-span-3 border-r-2 border-b-2 border-ink p-6 sm:p-8 bg-paper-alt flex flex-col gap-3 justify-center">
              <button
                data-testid="time-jump-btn"
                disabled={loading}
                onClick={() => goJump()}
                className="w-full bg-press-red text-paper font-headline font-bold uppercase tracking-widest text-lg px-5 py-4 border-2 border-ink shadow-hard hover:shadow-hard-hover transition-transform disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
                style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              >
                {loading ? "Assembling…" : "Time Jump →"}
              </button>
              <button
                data-testid="surprise-me-btn"
                disabled={loading}
                onClick={surprise}
                className="w-full bg-paper-surface text-ink font-mono uppercase tracking-widest text-sm px-5 py-3 border-2 border-ink shadow-hard-sm hover:shadow-hard-hover transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-70"
                style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              >
                <Sparkles size={16} /> Surprise Me
              </button>
              {entitlement && !entitlement.is_premium && entitlement.front_pages_limit > 0 && (
                <div className="mt-1 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted" data-testid="front-page-quota">
                  {entitlement.front_pages_remaining > 0
                    ? `${entitlement.front_pages_remaining} of ${entitlement.front_pages_limit} free front pages left today`
                    : (
                      <span className="text-press-red">
                        Daily free limit reached ·{" "}
                        <button
                          type="button"
                          onClick={() => { setPricingReason("quota_exhausted"); setShowPricing(true); }}
                          className="underline hover:no-underline"
                          data-testid="quota-upgrade-link"
                        >
                          Go Premium →
                        </button>
                      </span>
                    )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Iconic dates row — filtered by selected region */}
        {(() => {
          const iconicList = getIconicList(region);
          const regionMeta = regions.find((r) => r.code === region) || { name: region, flag: "" };
          const hero = iconicList[0];
          const rest = iconicList.slice(1);
          return (
        <section className="mt-14" data-testid="iconic-section">
          <div className="flex flex-wrap items-end justify-between border-b-2 border-ink pb-2 mb-5 gap-3">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-press-red mb-1">— The Vault —</div>
              <h3 className="font-headline text-3xl sm:text-4xl font-black uppercase leading-none tracking-tighter" data-testid="iconic-heading">
                Iconic Days
                <span className="font-mono text-sm sm:text-base align-middle text-ink-muted ml-3 normal-case tracking-normal">
                  {regionMeta.flag} {regionMeta.name}
                </span>
              </h3>
              <div className="font-body italic text-sm text-ink-muted mt-1">Hand-picked for {regionMeta.name}. Tap to time-jump.</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="go-compare-btn"
                onClick={() => navigate("/compare")}
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-alt shadow-hard-sm hover:shadow-hard-hover"
                style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              >
                <GitCompareArrows size={14} /> Compare Two Dates
              </button>
            </div>
          </div>

          {/* Hero card — the top pick, oversized */}
          {hero && (() => {
            const acc = ACCENT_STYLES[hero.accent] || ACCENT_STYLES.amber;
            return (
              <button
                data-testid="iconic-hero"
                onClick={() => { trackEvent("iconic_pick", { date: hero.date, region, label: hero.label, hero: true }); setDate(hero.date); goJump(hero.date, region); }}
                className="w-full text-left border-2 border-ink shadow-hard bg-paper-surface overflow-hidden hover:shadow-hard-hover mb-5"
                style={{ transition: "transform 200ms cubic-bezier(0.19, 1, 0.22, 1), box-shadow 200ms ease", transform: "translate(0,0)" }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translate(-2px, -2px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translate(0, 0)"}
              >
                <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
                  <div
                    className="p-6 sm:p-8 flex flex-col justify-between min-h-[180px]"
                    style={{ background: acc.bg, color: acc.text, borderRight: "2px solid var(--ink-primary)" }}
                  >
                    <div>
                      <div className="text-7xl leading-none" aria-hidden>{hero.icon}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] mt-4 opacity-70">Featured · {regionMeta.flag} {region}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest opacity-80" style={{ borderTop: `2px solid ${acc.stripe}`, paddingTop: "0.5rem", marginTop: "1rem" }}>
                        {hero.tag}
                      </div>
                      <div className="font-body italic text-sm mt-1 opacity-80">
                        {format(new Date(hero.date), "MMMM d, yyyy")}
                      </div>
                    </div>
                  </div>
                  <div className="p-6 sm:p-8 flex flex-col justify-center">
                    <div className="font-headline text-3xl sm:text-5xl font-black uppercase leading-[0.95] tracking-tighter">
                      {hero.label}
                    </div>
                    <p className="font-body text-base sm:text-lg leading-relaxed mt-3 max-w-xl text-ink-secondary">
                      {hero.hook}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-press-red">
                      <Sparkles size={14} /> Time-jump to this day →
                    </div>
                  </div>
                </div>
              </button>
            );
          })()}

          {/* Rest of the tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 border-t-2 border-l-2 border-ink">
            {rest.map((d, i) => {
              const acc = ACCENT_STYLES[d.accent] || ACCENT_STYLES.amber;
              return (
                <button
                  key={`${region}-${d.date}`}
                  data-testid={`iconic-date-${i}`}
                  onClick={() => { trackEvent("iconic_pick", { date: d.date, region, label: d.label }); setDate(d.date); goJump(d.date, region); }}
                  className="group text-left border-r-2 border-b-2 border-ink bg-paper-surface p-5 relative overflow-hidden"
                  style={{ transition: "background-color 200ms ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = acc.bg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: acc.stripe }} />
                  <div className="pl-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">{regionMeta.flag} {region}</div>
                      <div className="text-2xl leading-none" aria-hidden>{d.icon}</div>
                    </div>
                    <div className="font-headline text-xl font-bold mt-2 leading-[1.05] uppercase">
                      {d.label}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest mt-1" style={{ color: acc.stripe }}>
                      {d.tag}
                    </div>
                    <p className="font-body text-sm text-ink-secondary mt-2 leading-snug line-clamp-2">
                      {d.hook}
                    </p>
                    <div className="mt-3 pt-2 border-t border-dotted border-ink flex items-center justify-between">
                      <div className="font-body italic text-xs text-ink-muted">
                        {format(new Date(d.date), "MMM d, yyyy")}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-press-red opacity-0 group-hover:opacity-100" style={{ transition: "opacity 200ms ease" }}>
                        Jump →
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
          );
        })()}

        {/* Waitlist */}
        <WaitlistSection />

        {/* Monthly Sora video reel */}
        <MonthlyReel />

        {/* Daily digest signup */}
        <NewsletterSignup />



        <footer className="mt-16 pt-6 border-t-2 border-ink flex flex-col sm:flex-row justify-between gap-2 font-mono text-xs uppercase tracking-widest text-ink-muted">
          <span>TimeCapsule 24 · A daily portal to the past</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/contact" className="hover:text-press-red" data-testid="footer-contact-link">Contact</Link>
            <span aria-hidden="true">·</span>
            <Link to="/tv" className="hover:text-press-red">Watch on TV</Link>
            <span aria-hidden="true">·</span>
            <a href="mailto:support@timecapsule24.tv" className="hover:text-press-red">support@timecapsule24.tv</a>
          </div>
        </footer>
      </div>
      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        reason={pricingReason}
      />
    </div>
  );
}




function WaitlistSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setStatus("loading");
    try {
      await postWaitlist(email, "landing");
      trackEvent("waitlist_join", { email_domain: email.split("@")[1] || "" });
      setStatus("done");
    } catch {
      setStatus("idle");
    }
  };
  return (
    <section className="mt-14 border-2 border-ink bg-paper-alt p-6 sm:p-8" data-testid="waitlist-section">
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="border-2 border-ink p-2 bg-press-gold"><Mail size={20} /></div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-muted">Coming soon · TimeCapsule Pro</div>
            <div className="font-headline text-2xl font-bold leading-tight">Get notified when we launch saved timelines, physical postcards &amp; more.</div>
          </div>
        </div>
        {status === "done" ? (
          <div className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest border-2 border-ink bg-paper-surface px-4 py-3" data-testid="waitlist-done">
            <Check size={16} className="text-press-green" /> You&apos;re on the list.
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 items-stretch">
            <input
              data-testid="waitlist-email"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-paper-surface border-2 border-ink px-3 py-3 font-mono text-sm focus:outline-none min-w-[240px]"
            />
            <button
              data-testid="waitlist-submit"
              type="submit"
              disabled={status === "loading"}
              className="bg-ink text-paper font-mono uppercase tracking-widest text-sm px-5 py-3 border-2 border-ink shadow-hard-sm hover:shadow-hard-hover disabled:opacity-70"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              {status === "loading" ? "…" : "Join Waitlist →"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
